// SPDX-License-Identifier: GPL-3.0-or-later
// Copyright (C) 2026 Radiooooo

import { config } from "../core/config";
import { mountSignIn } from "../auth";
import { mountLanding, type Landing } from "../shell/landing";
import type { Foundation } from "./foundation";

/** The landing with its sign-in form and the third-party buttons it offers: the whole of what a visitor gets. */
export function mountLandingScreen(root: HTMLElement, foundation: Foundation): Landing {
    const landing = mountLanding(root, foundation.i18n);
    let page: { close(): void } | undefined;
    const signin = mountSignIn(landing.slots.signin, foundation.auth, foundation.session, foundation.i18n, {
        appleClientId: config.appleClientId,
        googleClientId: config.googleClientId,
        facebookAppId: config.facebookAppId,
    }, {
        // the terms and the privacy policy: the information layer, on that page, over the landing
        onPage: (slug) => {
            page?.close();
            void Promise.all([import("../information"), import("../information/view")]).then(([{ createInformationMode, createPagesApi }, { mountInformation }]) => {
                if (destroyed) return;
                const mode = createInformationMode(createPagesApi(foundation.plainClient), { locale: foundation.i18n.locale() });
                page = mountInformation(root, {
                    mode,
                    i18n: foundation.i18n,
                    version: config.appVersion,
                    signedIn: () => false,
                    onAccount: () => {},
                    onClose: () => { page = undefined; },
                });
                void mode.openPage(slug);
            }).catch((error) => console.warn("the information is unavailable", error));
        },
        // the reset link's address goes back to the root once it has done its work
        settle: () => { if (typeof history !== "undefined") history.replaceState(null, "", "/"); },
    });
    // Google's script is fetched once the card is on screen with its form, a
    // frame after it painted: a listener whose session is restored never sees
    // the card and never fetches it, and a visitor sees the card first.
    let visible = false;
    let busy = false;
    let destroyed = false;
    const paint = (fn: () => void) => { if (typeof requestAnimationFrame === "function") requestAnimationFrame(() => requestAnimationFrame(fn)); else setTimeout(fn, 0); };
    const shown = () => { if (visible && !busy) paint(() => { if (!destroyed && visible && !busy) signin.activate(); }); };
    return {
        ...landing,
        show(on) { landing.show(on); visible = on; shown(); },
        setBusy(on) { landing.setBusy(on); busy = on; shown(); },
        destroy() {
            destroyed = true;
            page?.close();
            signin.destroy();
            landing.destroy();
        },
    };
}
